import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FileButton,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import type {
  YukumlulukElleDurum,
  YukumlulukSatirDurumu,
  YukumlulukTakvimSatiri,
} from '@isg/validation';
import {
  useCiktiIndir,
  useCompanies,
  useWorkplacesTumu,
  useYukumlulukDurum,
  useYukumlulukEkSil,
  useYukumlulukEkYukle,
  useYukumlulukGozdenGecirme,
  useYukumlulukIsaret,
  useYukumlulukMod,
  useYukumlulukNot,
  useYukumlulukSerh,
  useYukumlulukSerhKaldir,
  useYukumlulukTakvimi,
  useKapilar,
  useSahaGunu,
} from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import type { RozetAnlami } from '../theme';
import { tarihGoster } from '../lib/format';
import {
  YUKUMLULUK_DURUM_ETIKETI,
  YUKUMLULUK_ELLE_DURUM_ETIKETI,
  YUKUMLULUK_KANIT_TIPI_ETIKETI,
  YUKUMLULUK_KAYNAK_ETIKETI,
  YUKUMLULUK_TIP_ETIKETI,
  yukumlulukRozetAnlami,
} from './yukumluluk-parts';

/**
 * YASAL UYGUNLUK TAKVİMİ (F4·S11·B — Kapsam §5.16 · §20.3b sayfa sözleşmesi;
 * IA **T8**: *"yükümlülük görevleri Yasal uygunluk'ta"*).
 *
 * > **BU EKRAN DENETİMDE GÖSTERİLİR.** P5'in *"detayda sekme yok — PDF'e
 * > giden şey sayfanın kendisidir"* kararının gerekçesi burada gerçek olur:
 * > listenin bölümleri çıktıya **aynı sırayla** iner.
 *
 * ⚠️ **TAKVİM BİR UYGUNLUK BEYANI DEĞİLDİR** ve bu cümle ekranın
 * **görünür** parçasıdır (aşağıdaki sorumluluk notu). Sistem *hesaplar ve
 * önerir*; nihai sorumluluk müşterinin İSG profesyonelindedir (Kapsam §8.5).
 * **Bu not kaldırılamaz** — pazarlık masasına konmaz.
 *
 * ⚠️ **EKRAN İKİNCİ KURAL YAZMAZ** (§2.16). Doğum kararı (sınıf kapısı +
 * eşik), periyot çözümlemesi, kademe ve durum **SUNUCUDA** hesaplanır; burada
 * yalnız **gösterim** vardır. *"İşyeri çok tehlikeliyse şu kalemi göster"*
 * diye dallanan kod, aynı kuralı ikinci kez tanımlar.
 *
 * ⚠️ **`mevcut === null` "KAYIT YOK" DEMEKTİR, SIFIR DEĞİL** (İ4). Ekran bunu
 * **boşluk olarak söyler** — *"Gerekli: ayda 100 dk · Görevlendirilen: kayıt
 * yok."* **Görünür boşluk, çalışıyor sanılan boşluktan iyidir.**
 */
const SUZGEC_ANAHTARI = 'yukumluluk-isyeri';
const SAYFA_BOYU = 20;

export function YukumlulukScreen(): React.JSX.Element {
  const companies = useCompanies();
  // ✏️ TUR 9 / B4 (T9-18): şirket başına fan-out yerine TEK istek.
  const isyeriSorgusu = useWorkplacesTumu();
  const isyerleri = useMemo(
    () =>
      (isyeriSorgusu.data ?? [])
        .map((w) => ({ value: w.id, label: w.ad }))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    [isyeriSorgusu.data],
  );

  const [workplaceId, setWorkplaceId] = useState<string | null>(() =>
    sessionStorage.getItem(SUZGEC_ANAHTARI),
  );
  const [tipSuzgeci, setTipSuzgeci] = useState<string>('tumu');
  const [sayfa, setSayfa] = useState(1);

  // İlk açılışta ilk işyerine düş; kullanıcının seçimi P4 gereği HATIRLANIR
  // (ama aşağıdaki gösterge sayesinde GÖRÜNÜR kalır).
  //
  // ⚠️ **VARSAYILAN BİR KEZ ATANIR ve bu bir test kolaylığı DEĞİL, bir
  // YARIŞ DÜZELTMESİDİR.** İşyerleri şirket başına AYRI sorgulardan **akarak**
  // gelir; liste akmaya devam ederken kullanıcı bir işyeri seçerse, sonraki
  // sorgu çözüldüğünde efekt tekrar koşar ve seçimi **sessizce ilk işyerine
  // geri alır** — kullanıcı *"seçtim, olmadı"* der ve tıklamasını suçlar.
  // Yol testi bunu yakaladı (§2.40'ın **hiçbir belirti üretmeyen** kolu).
  const varsayilanAtandi = useRef(false);
  useEffect(() => {
    if (varsayilanAtandi.current || workplaceId !== null || isyerleri.length === 0) return;
    varsayilanAtandi.current = true;
    setWorkplaceId(isyerleri[0]!.value);
  }, [isyerleri, workplaceId]);
  useEffect(() => {
    if (workplaceId) sessionStorage.setItem(SUZGEC_ANAHTARI, workplaceId);
  }, [workplaceId]);

  const takvim = useYukumlulukTakvimi(workplaceId);
  const isaret = useYukumlulukIsaret();
  const mod = useYukumlulukMod();
  const durum = useYukumlulukDurum();
  const notYaz = useYukumlulukNot();
  const serh = useYukumlulukSerh();
  const serhKaldir = useYukumlulukSerhKaldir();
  const gozdenGecirme = useYukumlulukGozdenGecirme();
  // Madde 302: "bugün" saha gününden (tek kaynak) — tarayıcı günü değil.
  const sahaGunu = useSahaGunu();
  const ekYukle = useYukumlulukEkYukle();
  const ekSil = useYukumlulukEkSil();
  const indir = useCiktiIndir();

  const satirlar = useMemo(() => {
    const hepsi = takvim.data?.satirlar ?? [];
    return tipSuzgeci === 'tumu' ? hepsi : hepsi.filter((s) => s.tip === tipSuzgeci);
  }, [takvim.data, tipSuzgeci]);
  const sayfadakiler = satirlar.slice((sayfa - 1) * SAYFA_BOYU, sayfa * SAYFA_BOYU);
  // Süzgeçten BAĞIMSIZ sayılır: gözden geçirme takvimin TAMAMINI kapsar.
  const acikSerhSayisi = (takvim.data?.satirlar ?? []).filter((x) => x.serh !== null).length;

  // ⚠️ İŞYERİ SEÇİMİ BİR SÜZGEÇ DEĞİL, EKRANIN EKSENİDİR (takvim işyeri
  // başına türetilir) — göstergeye girmez; giren yalnız TİP daraltmasıdır.
  const etkinSuzgecSayisi = tipSuzgeci === 'tumu' ? 0 : 1;
  // P13-b (madde 225): işaret kapısı SUNUCUDAN (`YUKUMLULUK_ISARET` — holding · şirket yön.
  // · uzman); bahçe işyeri seçilince kaydın kendi ucunda. Ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const isaretYazabilir = kapilar.data?.yukumlulukIsaretKapisi.yazabilir ?? false;

  /**
   * ✏️ Gezinti artçısı 2 / B4 — REDDİ YAKALANAN YAZMA GÖRÜNÜR (§2.40).
   * Mutasyonlar satırlar arasında paylaşılır; hata yalnız isteği atan satırda
   * çizilir (değişkenlerdeki kalem kimliğiyle) — aksi hâlde bir satırın 422'si
   * tüm satırlarda görünürdü.
   */
  function satirHatasi(kalemId: string): string | null {
    for (const m of [mod, durum, notYaz, serh]) {
      if (m.error && m.variables?.kalemId === kalemId) return m.error.message;
    }
    return null;
  }

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={4}>Yasal uygunluk</Title>
        {/* P2 — BİRİNCİL EYLEM ÇIKTI: bu ekranın varlık sebebi denetimde
            gösterilmektir; PDF'i ⋮ menüsüne gömmek onu saklamak olurdu. */}
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            disabled={!workplaceId}
            data-testid="yukumluluk-excel"
            onClick={() =>
              indir.mutate({
                yol: `/api/obligations/workplaces/${workplaceId!}/export`,
                yedekAd: 'yasal-uygunluk.xlsx',
              })
            }
          >
            Excel
          </Button>
          <Button
            size="xs"
            disabled={!workplaceId}
            data-testid="yukumluluk-pdf"
            onClick={() =>
              indir.mutate({
                yol: `/api/obligations/workplaces/${workplaceId!}/pdf`,
                yedekAd: 'yasal-uygunluk.pdf',
              })
            }
          >
            PDF
          </Button>
        </Group>
      </Group>

      <Group gap="sm" align="flex-end">
        <Select
          size="xs"
          label="İşyeri"
          // ⚠️ **ARANABİLİR: LİSTE MÜŞTERİYLE BÜYÜR.** Kurguda yedi işyeri var
          // ve kaydırmadan seçiliyor; elli sicilli bir holdingde aynı liste
          // *"görünüyor · çalışıyor · KULLANILAMIYOR"* sınıfına düşer (§2.40'ın
          // en sessiz kolu — kullanıcı bunu arıza olarak bildiremez bile).
          searchable
          comboboxProps={{ withinPortal: true }}
          data={isyerleri}
          value={workplaceId}
          // ⚠️ **BOŞ SEÇİM YOK — EKSENİN BOŞU OLMAZ.** Takvim işyeri başına
          // türetilir; seçimi `null`a düşürmek ekranı sebepsiz boşaltır
          // (aranabilir seçicide bir tuş vuruşu bunu tetikleyebiliyordu).
          onChange={(v) => {
            if (!v) return;
            setWorkplaceId(v);
            setSayfa(1);
          }}
          data-testid="yukumluluk-isyeri-secimi"
          w={260}
        />
        <Select
          size="xs"
          label="Tip"
          data={[
            { value: 'tumu', label: 'Tümü' },
            { value: 'periyodik', label: YUKUMLULUK_TIP_ETIKETI.periyodik! },
            { value: 'surekli', label: YUKUMLULUK_TIP_ETIKETI.surekli! },
            { value: 'modul_takipli', label: YUKUMLULUK_TIP_ETIKETI.modul_takipli! },
          ]}
          value={tipSuzgeci}
          onChange={(v) => {
            setTipSuzgeci(v ?? 'tumu');
            setSayfa(1);
          }}
          data-testid="yukumluluk-tip-suzgeci"
          w={200}
        />
      </Group>

      {/* P4 — süzgeç HATIRLANIR ama GÖRÜNÜR: kullanıcı neyi elinde tuttuğunu
          bilmeden "kalemlerim kaybolmuş" der; çıktı da aynı süzgeci taşır. */}
      <SuzgecGostergesi
        etkinSayisi={etkinSuzgecSayisi}
        onTemizle={() => {
          setTipSuzgeci('tumu');
          setSayfa(1);
        }}
      />

      <QueryStatus queries={[companies, takvim]} />

      {takvim.data && (
        <Card withBorder padding="sm">
          <Group gap="lg">
            <Text size="sm">
              <Text span fw={600}>
                {takvim.data.companyAd}
              </Text>{' '}
              — {takvim.data.workplaceAd}
            </Text>
            <Text size="sm" c="dimmed" data-testid="yukumluluk-calisan-sayisi">
              Çalışan sayısı: {takvim.data.calisanSayisi}
            </Text>
          </Group>
          {/* ✏️ **TUR 8 / B4 — GÖZDEN GEÇİRME BANDI.** *"Bu değerlendirmeye en
              son ne zaman bakıldı?"* Vade kararı SUNUCUDAN gelir; ekran
              *"12'den büyük mü"* diye dallanmaz (ikinci kural yazmak olurdu). */}
          <Group gap="sm" mt="xs" align="center">
            <Text size="sm" c="dimmed" data-testid="yukumluluk-gozden-gecirme">
              {takvim.data.sonGozdenGecirme
                ? `Son gözden geçirme: ${tarihGoster(takvim.data.sonGozdenGecirme.tarih)}`
                : 'Henüz gözden geçirilmedi'}
            </Text>
            {takvim.data.gozdenGecirmeVadesiGecti && (
              <Rozet anlam={ANLAM.gecikmis} testId="yukumluluk-gozden-gecirme-vadesi">
                {takvim.data.gozdenGecirmeAyFarki} aydır güncellenmedi
              </Rozet>
            )}
            {isaretYazabilir && (
              <Button
                size="xs"
                variant="light"
                loading={gozdenGecirme.isPending}
                disabled={!sahaGunu.data}
                data-testid="yukumluluk-gozden-gecir"
                onClick={() =>
                  // Madde 302: gözden geçirme günü SAHA GÜNÜ (sunucudan) —
                  // gün gelmeden düğme kapalı (kabuk ön yükler; pratikte anlık).
                  gozdenGecirme.mutate({ workplaceId: workplaceId!, tarih: sahaGunu.data! })
                }
              >
                Gözden geçirdim
              </Button>
            )}
            {kapilar.data && (
              <KapiSebebi
                durum={kapilar.data.yukumlulukIsaretKapisi}
                nesne="Yükümlülük işaretini"
                fiil="yazar"
                testId="yukumluluk-isaret-yazamama-sebebi"
              />
            )}
          </Group>
          {/* ⚠️ ŞERHİN VADESİ VARDIR: gözden geçirme onları DÜŞÜRÜR. Bu cümle
              düğmenin ne yapacağını söyler — basıldıktan sonra değil ÖNCE
              (§2.40: kullanıcı sonucu tahmin etmek zorunda kalmaz). */}
          {isaretYazabilir && acikSerhSayisi > 0 && (
            <Text size="xs" c="dimmed" data-testid="yukumluluk-serh-uyarisi">
              Gözden geçirince yürürlükteki {acikSerhSayisi} şerh düşer.
            </Text>
          )}
        </Card>
      )}

      {takvim.data && satirlar.length === 0 && (
        <BosListeNotu mesaj="Bu işyerinde bu süzgeçle doğan yükümlülük kalemi yok." />
      )}

      {sayfadakiler.length > 0 && (
        <Table striped highlightOnHover data-testid="yukumluluk-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Yükümlülük</Table.Th>
              <Table.Th>Kaynak</Table.Th>
              <Table.Th>Tip</Table.Th>
              <Table.Th>Durum</Table.Th>
              <Table.Th>Periyot</Table.Th>
              <Table.Th>Gereken / Görevlendirilmiş</Table.Th>
              {/* TUR 8 / B — kalemin bu işyerindeki DEĞERLENDİRMESİ. */}
              <Table.Th>Değerlendirme</Table.Th>
              {/* ✏️ TUR 8 / D1 + D2 — "denetimde ne göstereceğim?" */}
              <Table.Th>Kanıt</Table.Th>
              <Table.Th>Not</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sayfadakiler.map((s) => (
              <Table.Tr key={s.kalemId} data-testid="yukumluluk-satiri">
                <Table.Td>
                  <Text size="sm">{s.ad}</Text>
                  {s.dayanak && (
                    <Text size="xs" c="dimmed">
                      {s.dayanak}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{YUKUMLULUK_KAYNAK_ETIKETI[s.kaynak]}</Table.Td>
                <Table.Td>{YUKUMLULUK_TIP_ETIKETI[s.tip]}</Table.Td>
                <Table.Td>
                  <YukumlulukRozeti satir={s} />
                </Table.Td>
                <Table.Td>{s.periyotAy === null ? '—' : `${s.periyotAy} ay`}</Table.Td>
                <Table.Td>
                  <GerekenMevcut satir={s} />
                </Table.Td>
                <Table.Td>
                  <DegerlendirmeHucresi
                    satir={s}
                    yazabilir={isaretYazabilir}
                    calisiyor={mod.isPending || durum.isPending || serh.isPending}
                    notKaydediliyor={notYaz.isPending && notYaz.variables?.kalemId === s.kalemId}
                    hata={satirHatasi(s.kalemId)}
                    onMod={(deger) =>
                      mod.mutate({ workplaceId: workplaceId!, kalemId: s.kalemId, mod: deger })
                    }
                    onDurum={(deger) =>
                      durum.mutate({
                        workplaceId: workplaceId!,
                        kalemId: s.kalemId,
                        durum: deger,
                      })
                    }
                    onNot={(notu, bitti) =>
                      notYaz.mutate(
                        { workplaceId: workplaceId!, kalemId: s.kalemId, notu },
                        { onSuccess: bitti },
                      )
                    }
                    onSerh={(deger, gerekce) =>
                      serh.mutate({
                        workplaceId: workplaceId!,
                        kalemId: s.kalemId,
                        durum: deger,
                        gerekce,
                      })
                    }
                    onSerhKaldir={(serhId) =>
                      serhKaldir.mutate({ workplaceId: workplaceId!, serhId })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <KanitHucresi
                    satir={s}
                    yazabilir={isaretYazabilir}
                    calisiyor={ekYukle.isPending || ekSil.isPending}
                    hata={(ekYukle.error ?? ekSil.error)?.message ?? null}
                    onYukle={(dosya) =>
                      ekYukle.mutate({ workplaceId: workplaceId!, kalemId: s.kalemId, dosya })
                    }
                    onSil={(ekId) => ekSil.mutate({ workplaceId: workplaceId!, ekId })}
                  />
                </Table.Td>
                <Table.Td>
                  <NotHucresi
                    satir={s}
                    yazabilir={isaretYazabilir}
                    onIsaret={(deger) =>
                      isaret.mutate({
                        workplaceId: workplaceId!,
                        kalemId: s.kalemId,
                        elleIsaret: deger,
                      })
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={SAYFA_BOYU}
        toplam={satirlar.length}
        onDegis={setSayfa}
      />

      {/* ⚠️ BU NOT KALDIRILMAZ (Kapsam §8.5) — takvim bir uygunluk beyanı
          değildir ve ekran bunu SÖYLEMEK zorundadır. */}
      <Alert color={ANLAM.bilgi} variant="light" data-testid="yukumluluk-sorumluluk-notu">
        <Text size="sm">
          Bu liste bir uygunluk beyanı değildir. Sistem yürürlükteki kural setine göre neyin ne
          zaman gerektiğini <b>hesaplar ve önerir</b>; neyin eksik göründüğünü gösterir. Nihai
          değerlendirme ve sorumluluk işyerinin İSG profesyonelindedir.
        </Text>
      </Alert>
    </Stack>
  );
}

function YukumlulukRozeti({ satir }: { satir: YukumlulukTakvimSatiri }): React.JSX.Element {
  const anlam: RozetAnlami = yukumlulukRozetAnlami(satir.durum);
  return (
    <Rozet anlam={anlam} testId="yukumluluk-rozeti">
      {YUKUMLULUK_DURUM_ETIKETI[satir.durum as YukumlulukSatirDurumu]}
    </Rozet>
  );
}

/**
 * ⚠️ **İ4 — GÖRÜNÜR BOŞLUK.** `görevlendirilmiş === null` iken *"0"* yazmak,
 * sistemin bilmediği bir şeyi bildiğini iddia etmesidir; ekran **"kayıt
 * yok"** der.
 *
 * ✏️ **TUR 8 / A2 — ÜÇ SAYI GÖRÜNÜR.** *"3 gerekli · 3 görevlendirilmiş ·
 * 1'i şartsız"*: sayının tutması yükümlülüğü karşılamaz, çünkü sertifikası
 * olmayan ilkyardımcı **denetimde ilkyardımcı sayılmaz** (A3 — rozet de
 * *gecikmiş* yanar; bu satır o rozetin **sebebini** söyler, ikinci bir kural
 * yazmaz).
 */
function GerekenMevcut({ satir }: { satir: YukumlulukTakvimSatiri }): React.JSX.Element {
  if (satir.gereken === null) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  const sartsiz =
    satir.gorevlendirilmis !== null && satir.yeterli !== null
      ? satir.gorevlendirilmis - satir.yeterli
      : 0;
  return (
    <Stack gap={0}>
      <Text size="sm">Gerekli: {gerekenMetni(satir)}</Text>
      <Text size="sm" c={satir.gorevlendirilmis === null ? 'dimmed' : undefined}>
        {satir.gorevlendirilmis === null
          ? 'Görevlendirilen: kayıt yok'
          : `Görevlendirilmiş: ${satir.gorevlendirilmis}`}
      </Text>
      {sartsiz > 0 && (
        <Text size="sm" c={ANLAM.dikkat}>
          {sartsiz}&apos;i şartsız
        </Text>
      )}
    </Stack>
  );
}

const BIRIM_ETIKETI: Record<string, string> = {
  dakika_calisan_ay: 'dk/ay',
  adet: 'kişi',
  bir_kisi_basina_calisan: 'kişi',
};

function gerekenMetni(satir: YukumlulukTakvimSatiri): string {
  if (satir.gereken === null) return '—';
  const birim = satir.gerekenBirim ? (BIRIM_ETIKETI[satir.gerekenBirim] ?? '') : '';
  return `${satir.gereken} ${birim}`.trim();
}

/**
 * NOT HÜCRESİ — üç şey taşır ve üçü de **dürüstlük kaydıdır**:
 *   · #5'in NİTELİK KAPISI (A/B/C) — bir periyot ya da miktar DEĞİL,
 *     *kimin görevlendirilebileceği*
 *   · modül-takip iddiasının **kod karşılığı** — gösterilemeyen iddia burada
 *     *"takip edilmiyor"* der ve **gizlenmez**
 *   · **İ5 elle işareti** — kural motoru YOK, işaret elle konur ve altı ay
 *     dolunca **SORGULANIR, ENGELLENMEZ**
 */
/**
 * **TUR 8 / B — DEĞERLENDİRME HÜCRESİ: İKİ MOD, TEK CEVAP.**
 *
 * ⚠️ **BİR KALEMİN DURUMUNUN İKİ CEVABI OLMAZ.** Elle modda kullanıcının
 * işareti geçerlidir; bağlı modda sistemin türetmesi geçerlidir ve elle
 * işaretleme **kapanır** — bu ekranda da öyle görünür (kontrol çizilmez;
 * §2.40'ın *"çalışamayacaksa görünmez ya da salt okunur olur"* maddesi).
 *
 * ⚠️ **BAĞLANAMAYAN KALEMDE ANAHTAR PASİF ve SEBEBİ İPUCUNDA.** *"Basılınca
 * hata verir"* en kötü seçenektir (§2.40): kullanıcı kendi hatası mı sistem
 * arızası mı ayırt edemez. Karar SUNUCUDAN gelir (`baglanabilir`) — ekran
 * ikinci bir koşul yazmaz.
 *
 * ⚠️ **ŞERH TÜRETİLENİ SİLMEZ:** rozet (soldaki *Durum* kolonu) sistemin
 * cevabını göstermeye devam eder, şerh onun **yanında** durur. İkisi de
 * görünür kalır — denetimde iz budur.
 *
 * ✏️ **GEZİNTİ ARTÇISI 2 / B4 (ürün sahibi 07.09.2026):**
 * - **B4.1 — SATIR DİKEYDE DARALDI:** anahtar + işaretleyen tek satırda;
 *   değerlendirme ve not **YAN YANA** (eskiden dört satır alt alta).
 * - **B4.2 — GEREKÇE ALANI KALKTI, NOT KALDI:** gerekçeyi kanıt belgesi taşır;
 *   not zorunlu değildir, **moddan bağımsızdır** ve **kendi düğmesiyle**
 *   kaydedilir. ⚠️ **Eski kutunun iki arızası:** seçimden SONRA yazılan metin
 *   hiç kaydedilmiyordu (kutu yerel durumdu, gönderim yalnız seçimde koşuyordu)
 *   ve kutu boşken sunucuya *"Gerekçe girilmedi"* UYDURULUYORDU.
 * - ⚠️ **ŞERH NOTTAN AYRI BLOKTUR:** gerekçesi zorunlu, etiketi *"Şerh"* —
 *   iki metin kutusu aynı şeyi söylüyormuş gibi durmaz.
 */
function DegerlendirmeHucresi({
  satir,
  yazabilir,
  calisiyor,
  notKaydediliyor,
  hata,
  onMod,
  onDurum,
  onNot,
  onSerh,
  onSerhKaldir,
}: {
  satir: YukumlulukTakvimSatiri;
  yazabilir: boolean;
  calisiyor: boolean;
  notKaydediliyor: boolean;
  hata: string | null;
  onMod: (deger: 'elle' | 'bagli') => void;
  onDurum: (deger: YukumlulukElleDurum | null) => void;
  onNot: (notu: string | null, bitti: () => void) => void;
  onSerh: (deger: YukumlulukElleDurum, gerekce: string) => void;
  onSerhKaldir: (serhId: string) => void;
}): React.JSX.Element {
  const [gerekce, setGerekce] = useState('');
  const [serhAcik, setSerhAcik] = useState(false);
  // B4.2 — NOT TASLAĞI: `null` = dokunulmadı (sunucudaki değer gösterilir).
  const [notTaslagi, setNotTaslagi] = useState<string | null>(null);
  const notDegeri = notTaslagi ?? satir.notu ?? '';
  const notDegisti = notTaslagi !== null && notTaslagi.trim() !== (satir.notu ?? '');

  const anahtar = (
    <Switch
      size="xs"
      label={satir.mod === 'bagli' ? 'Sistemden türetiliyor' : 'Elle işaretleniyor'}
      checked={satir.mod === 'bagli'}
      disabled={!yazabilir || calisiyor || (satir.mod === 'elle' && !satir.baglanabilir)}
      data-testid="yukumluluk-mod"
      onChange={(e) => onMod(e.currentTarget.checked ? 'bagli' : 'elle')}
    />
  );

  return (
    <Stack gap={4} data-testid="yukumluluk-degerlendirme">
      {/* B4.1 — ÜST SATIR: mod anahtarı + işaretleyen/tarih YAN YANA. */}
      <Group gap="xs" wrap="wrap" align="center">
        {/* Pasif anahtarın SEBEBİ ipucunda — sebepsiz pasiflik kullanıcıya
            sistemin bozuk olduğunu düşündürür (İ4 · §2.40). */}
        {satir.mod === 'elle' && !satir.baglanabilir && satir.baglanamamaSebebi ? (
          <Tooltip label={satir.baglanamamaSebebi} multiline w={240}>
            <div data-testid="yukumluluk-baglanamaz">{anahtar}</div>
          </Tooltip>
        ) : (
          anahtar
        )}
        {satir.mod === 'elle' && satir.isaretleyenAd && satir.isaretTarihi && (
          <Text size="xs" c="dimmed">
            {satir.isaretleyenAd} · {tarihGoster(satir.isaretTarihi)}
          </Text>
        )}
      </Group>

      {/* B4.1 — ALT SATIR: DEĞERLENDİRME ve NOT YAN YANA. */}
      <Group gap={6} wrap="wrap" align="flex-start">
        {satir.mod === 'elle' && (
          <Select
            size="xs"
            w={150}
            placeholder="İşaretlenmedi"
            clearable
            comboboxProps={{ withinPortal: true }}
            data={[
              { value: 'uygun', label: YUKUMLULUK_ELLE_DURUM_ETIKETI.uygun },
              { value: 'uygun_degil', label: YUKUMLULUK_ELLE_DURUM_ETIKETI.uygun_degil },
              { value: 'kapsam_disi', label: YUKUMLULUK_ELLE_DURUM_ETIKETI.kapsam_disi },
            ]}
            value={satir.elleDurum}
            disabled={!yazabilir || calisiyor}
            data-testid="yukumluluk-elle-durum"
            // ⚠️ B4.2 — seçim YALNIZ durumu yazar; gerekçe UYDURULMAZ.
            onChange={(deger) => onDurum((deger as YukumlulukElleDurum | null) ?? null)}
          />
        )}
        {satir.mod === 'bagli' && satir.serh === null && yazabilir && !serhAcik && (
          <Button
            size="compact-xs"
            variant="subtle"
            data-testid="yukumluluk-serh-ac"
            onClick={() => setSerhAcik(true)}
          >
            Şerh düş
          </Button>
        )}
        {yazabilir ? (
          <Group gap={4} wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 160 }}>
            <Textarea
              size="xs"
              autosize
              minRows={1}
              maxRows={4}
              placeholder="Not"
              aria-label="Not"
              value={notDegeri}
              data-testid="yukumluluk-not"
              style={{ flex: 1 }}
              onChange={(e) => {
                // D1 — değer olayın İÇİNDE okunur (React olayı bitince null).
                const deger = e.currentTarget.value;
                setNotTaslagi(deger);
              }}
            />
            {notDegisti && (
              <Button
                size="compact-xs"
                loading={notKaydediliyor}
                data-testid="yukumluluk-not-kaydet"
                onClick={() => onNot(notDegeri.trim() || null, () => setNotTaslagi(null))}
              >
                Kaydet
              </Button>
            )}
          </Group>
        ) : (
          satir.notu && (
            <Text size="xs" data-testid="yukumluluk-not-metni">
              Not: {satir.notu}
            </Text>
          )
        )}
      </Group>

      {hata && (
        <Text size="xs" c={ANLAM.engel} role="alert" data-testid="yukumluluk-satir-hata">
          {hata}
        </Text>
      )}

      {/* ⚠️ ŞERH NOTTAN AYRI BLOK — gerekçesi zorunlu, denetim izi. */}
      {satir.mod === 'bagli' && satir.serh === null && yazabilir && serhAcik && (
        <Stack gap={4} data-testid="yukumluluk-serh-formu">
          <Textarea
            size="xs"
            autosize
            minRows={1}
            placeholder="Şerh gerekçesi (zorunlu)"
            value={gerekce}
            data-testid="yukumluluk-serh-gerekce"
            onChange={(e) => setGerekce(e.currentTarget.value)}
          />
          <Group gap={4}>
            <Button
              size="compact-xs"
              disabled={gerekce.trim().length === 0 || calisiyor}
              data-testid="yukumluluk-serh-kaydet"
              onClick={() => {
                onSerh('uygun', gerekce.trim());
                setGerekce('');
                setSerhAcik(false);
              }}
            >
              Şerhi kaydet
            </Button>
            <Button size="compact-xs" variant="subtle" onClick={() => setSerhAcik(false)}>
              Vazgeç
            </Button>
          </Group>
        </Stack>
      )}

      {satir.serh && (
        <Stack gap={2} data-testid="yukumluluk-serh">
          <Text size="xs" c={ANLAM.dikkat}>
            Şerh: {YUKUMLULUK_ELLE_DURUM_ETIKETI[satir.serh.durum]}
          </Text>
          <Text size="xs" c="dimmed">
            {satir.serh.gerekce}
          </Text>
          <Text size="xs" c="dimmed">
            {satir.serh.koyanAd ?? '—'} · {tarihGoster(satir.serh.tarih)}
          </Text>
          {yazabilir && (
            <Button
              size="compact-xs"
              variant="subtle"
              data-testid="yukumluluk-serh-kaldir"
              onClick={() => onSerhKaldir(satir.serh!.id)}
            >
              Şerhi kaldır
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * ✏️ **TUR 8 / D1 + D2 — KANIT HÜCRESİ: *"DENETİMDE NE GÖSTERECEĞİM?"***
 *
 * Sınıf (D1) *ne tür* der — `belge` · `form_kaydi` · `kayit_sayisi` ·
 * `beyan`; ekler (D2) *hangisi* der.
 *
 * ⚠️ **SINIFSIZ KALEMDE HÜCRE BOŞ KALIR ve boşluğun kendisi bilgidir** (İ4):
 * oraya *"belirsiz"* yazmak, kararın verilmiş ama içeriğinin belirsiz
 * olduğunu düşündürürdü — oysa karar **henüz verilmedi** (#31 · #33).
 *
 * ⚠️ **SINIF BİR BEKLENTİDİR, BİR KAPI DEĞİL:** `belge` olmayan kaleme de ek
 * yüklenir. Sınıfa göre düğmeyi gizlemek, müşterinin elindeki gerçek bir
 * kanıtı *"beklenmiyor"* diye geri çevirirdi — ve o an kullanıcı dosyayı
 * **hiçbir yere koyamaz.**
 *
 * ⚠️ **YÜKLEME HATASI GÖRÜNÜR** (§2.40): reddi yakalamayan bir gönderim
 * hatayı tarayıcıya düşürür ve kullanıcı **hiçbir şey görmez** — tur 3
 * artçısının form vakasının aynısı.
 */
function KanitHucresi({
  satir,
  yazabilir,
  calisiyor,
  hata,
  onYukle,
  onSil,
}: {
  satir: YukumlulukTakvimSatiri;
  yazabilir: boolean;
  calisiyor: boolean;
  hata: string | null;
  onYukle: (dosya: File) => void;
  onSil: (ekId: string) => void;
}): React.JSX.Element {
  return (
    <Stack gap={4}>
      {satir.kanitTipi && (
        <Rozet anlam="bilgi" testId="yukumluluk-kanit-tipi">
          {YUKUMLULUK_KANIT_TIPI_ETIKETI[satir.kanitTipi]}
        </Rozet>
      )}
      {satir.kanitMetni && (
        <Text size="xs" c="dimmed">
          {satir.kanitMetni}
        </Text>
      )}
      {satir.ekler.map((ek) => (
        <Group key={ek.id} gap={4} wrap="nowrap" data-testid="yukumluluk-ek">
          <Text size="xs" style={{ wordBreak: 'break-all' }}>
            {ek.dosyaAdi} · {(ek.boyut / 1024).toFixed(0)} KB
          </Text>
          {yazabilir && (
            <Button
              size="compact-xs"
              variant="subtle"
              color={ANLAM.engel}
              disabled={calisiyor}
              data-testid="yukumluluk-ek-sil"
              onClick={() => onSil(ek.id)}
            >
              Sil
            </Button>
          )}
        </Group>
      ))}
      {yazabilir && (
        <FileButton onChange={(dosya) => dosya && onYukle(dosya)}>
          {(props) => (
            <Button
              {...props}
              size="compact-xs"
              variant="subtle"
              loading={calisiyor}
              data-testid="yukumluluk-ek-yukle"
            >
              Kanıt yükle
            </Button>
          )}
        </FileButton>
      )}
      {hata && (
        <Text size="xs" c={ANLAM.engel} role="alert" data-testid="yukumluluk-ek-hata">
          {hata}
        </Text>
      )}
    </Stack>
  );
}

function NotHucresi({
  satir,
  yazabilir,
  onIsaret,
}: {
  satir: YukumlulukTakvimSatiri;
  yazabilir: boolean;
  onIsaret: (deger: boolean) => void;
}): React.JSX.Element {
  return (
    <Stack gap={4}>
      {satir.nitelik && (
        <Text size="xs">
          Nitelik: <b>{satir.nitelik}</b>
        </Text>
      )}
      {/* ✏️ **TUR 8 / F5 BULGUSU — "KAYIT YOK" İKİ FARKLI ŞEY SÖYLÜYORDU.**
          Periyodik kalemin görev tanımı YAPILMIŞ ama vadesi henüz açılma
          penceresine (30 gün) girmemişse satır `kayit_yok` olur — yani
          *hiç tanımlanmamış* bir kalemle **aynı** görünür. Kullanıcı bu ikisini
          ayırt edemez ve tanımladığı görevi *"yine de eksik"* sanır.
          ⚠️ **ÇÖZÜM YENİ BİR DURUM DEĞERİ DEĞİL, BİR CÜMLEDİR:** yeni bir
          token, onu okuyan HER yüklemin (rozet · çıktı · PDF · rapor) tek tek
          gözden geçirilmesini isterdi (`cancelled` dersi) — burada eksik olan
          şey bir durum değil, bir AÇIKLAMA. */}
      {satir.tip === 'periyodik' && satir.gorevAtamaId !== null && satir.sonrakiVade === null && (
        <Text size="xs" c="dimmed" data-testid="yukumluluk-gorev-tanimli">
          Görev tanımlı; bu dönemin vadesi henüz açılmadı.
        </Text>
      )}
      {satir.takipDurumu === 'takip_edilmiyor' && (
        <Text size="xs" c="dimmed" data-testid="yukumluluk-takip-edilmiyor">
          Bu kalem hiçbir modülde takip edilmiyor.
        </Text>
      )}
      {satir.takipDurumu === 'kismi' && (
        <Text size="xs" c="dimmed">
          Modül kısmen takip ediyor.
        </Text>
      )}
      {/* ✏️ TUR 8 / F3 — NE EKSİK OLDUĞUNU SÖYLER. Eskiden bu satırda hiçbir
          şey yoktu; ayrıntı yanıtta `takipKaniti` olarak geliyor ama **iç kod
          adları** taşıyordu (`deriveYetkinlikBitis` · `person_competency.
          gecerlilik_bitis`) ve ekrana konsaydı bir HATA KODU gibi görünürdü
          (§2.40'ın belge-referansı kolunun kardeşi). Not artık D2 dilinde. */}
      {satir.takipNotu && (
        <Text size="xs" c="dimmed" data-testid="yukumluluk-takip-notu">
          {satir.takipNotu}
        </Text>
      )}
      {satir.elleIsaretMetni &&
        (yazabilir ? (
          <Checkbox
            size="xs"
            label={satir.elleIsaretMetni}
            checked={satir.elleIsaret}
            data-testid="yukumluluk-elle-isaret"
            onChange={(e) => onIsaret(e.currentTarget.checked)}
          />
        ) : (
          <Text size="xs">
            {satir.elleIsaretMetni}: {satir.elleIsaret ? 'işaretli' : 'işaretsiz'}
          </Text>
        ))}
      {satir.isaretSorgulanmali && (
        <Text size="xs" c={ANLAM.gecikmis} data-testid="yukumluluk-isaret-sorgusu">
          İşaret altı aydan uzun süredir duruyor — gözden geçirin.
        </Text>
      )}
    </Stack>
  );
}
